import { useSyncExternalStore } from 'react';
import { TOOLS } from '../seo/seoData';

const FAVORITES_KEY = 'mrencoder:favorites';
const RECENTS_KEY = 'mrencoder:recents';
const MAX_RECENTS = 6;
const EMPTY: string[] = [];

const listeners = new Set<() => void>();
const cache = new Map<string, string[]>();

const load = (key: string): string[] => {
  if (cache.has(key)) return cache.get(key)!;
  let value = EMPTY;
  try {
    const parsed = JSON.parse(localStorage.getItem(key) ?? '[]');
    // Drop ids of tools that no longer exist.
    if (Array.isArray(parsed)) value = parsed.filter((id) => TOOLS.some((tool) => tool.id === id));
  } catch {
    // Storage may be unavailable (private mode) or corrupted; start empty.
  }
  cache.set(key, value);
  return value;
};

const save = (key: string, value: string[]) => {
  cache.set(key, value);
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Best effort only.
  }
  listeners.forEach((listener) => listener());
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  // Keep other open tabs in sync.
  const onStorage = (event: StorageEvent) => {
    if (event.key === FAVORITES_KEY || event.key === RECENTS_KEY) {
      cache.delete(event.key);
      listener();
    }
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
};

export const useFavorites = () => {
  const favorites = useSyncExternalStore(subscribe, () => load(FAVORITES_KEY), () => EMPTY);
  const toggleFavorite = (id: string) =>
    save(FAVORITES_KEY, favorites.includes(id) ? favorites.filter((item) => item !== id) : [...favorites, id]);
  return { favorites, toggleFavorite, isFavorite: (id: string) => favorites.includes(id) };
};

export const useRecents = () => useSyncExternalStore(subscribe, () => load(RECENTS_KEY), () => EMPTY);

export const recordRecent = (id: string) => {
  const recents = load(RECENTS_KEY);
  if (recents[0] === id) return;
  save(RECENTS_KEY, [id, ...recents.filter((item) => item !== id)].slice(0, MAX_RECENTS));
};
